import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatApiError, API, mrApi, mrSession } from "@/lib/apiClient";
import { displayDoctorName, formatIST } from "@/lib/utils";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { DoctorPhoto } from "@/components/DoctorPhoto";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Loader2, ArrowLeft, Download, Pencil, ChevronLeft, ChevronRight, ClipboardList,
} from "lucide-react";

// MR portal: pledges submitted from this MR's account — lets them check
// before re-entering a doctor, fix a wrong name, and download certificates.
export default function MrPledgesPage() {
  const navigate = useNavigate();
  const [mr, setMr] = useState(mrSession.info());
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // pledge being renamed
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const limit = 20;

  useEffect(() => {
    if (!mrSession.token()) { navigate("/mr/login"); return; }
    mrApi.get("/mr/me")
      .then((r) => { setMr(r.data); mrSession.update(r.data); })
      .catch(() => { mrSession.clear(); navigate("/mr/login"); });
  }, [navigate]);

  const load = useCallback(() => {
    setLoading(true);
    mrApi.get("/mr/pledges", { params: { search, page, limit } })
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err.response?.status === 401) { mrSession.clear(); navigate("/mr/login"); }
      })
      .finally(() => setLoading(false));
  }, [search, page, navigate]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openEdit = (p) => {
    setEditing(p);
    setNewName(p.name);
  };

  const saveName = async () => {
    if (newName.trim().length < 2) { toast.error("Enter the doctor's full name."); return; }
    setSaving(true);
    try {
      await mrApi.patch(`/mr/pledges/${editing.id}`, { name: newName.trim() });
      toast.success("Name updated — the certificate uses the new name too");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <Link to="/pledge" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2]" data-testid="my-pledges-back">
            <ArrowLeft className="w-4 h-4" /> Back to pledge form
          </Link>
          {mr && <span className="text-sm text-slate-500">{mr.name} · MR {mr.emp_code}</span>}
        </div>

        <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8" data-testid="my-pledges-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-display font-extrabold text-2xl text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-[#0056D2]" /> My Pledges ({data.total})
            </h1>
            <div className="relative min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="my-pledges-search"
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                placeholder="Search name, reg no, city…"
                className="pl-9"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">Check here before adding a doctor to avoid repeat entries.</p>

          <div className="mt-5 space-y-3">
            {loading ? (
              <p className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></p>
            ) : data.items.length === 0 ? (
              <p className="text-center py-10 text-slate-400 text-sm">
                {search ? "No pledges match your search." : "You haven't submitted any pledges yet."}
              </p>
            ) : (
              data.items.map((p) => (
                <div key={p.id} data-testid={`my-pledge-${p.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 p-4">
                  <DoctorPhoto pledge={p} size="w-12 h-12" />
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-slate-900 truncate">{displayDoctorName(p.name)}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {p.specialty} · {p.city}, {p.state} · Reg: {p.reg_no}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{formatIST(p.created_at)} · {p.certificate_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid={`edit-name-${p.id}`}
                      onClick={() => openEdit(p)}
                      size="sm" variant="outline" className="rounded-full border-slate-300 text-slate-600"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit name
                    </Button>
                    <a href={`${API}/certificate/${p.certificate_id}`} data-testid={`cert-${p.id}`}>
                      <Button size="sm" className="rounded-full bg-[#138808] hover:bg-green-700 text-white">
                        <Download className="w-3.5 h-3.5 mr-1" /> Certificate
                      </Button>
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-5 flex items-center justify-between text-sm text-slate-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} size="sm" variant="outline" className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
              <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} size="sm" variant="outline" className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit doctor name */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogTitle className="font-display font-bold text-xl text-slate-900">Correct Doctor Name</DialogTitle>
          <div className="space-y-4">
            <div>
              <Label htmlFor="fix-name" className="text-sm font-medium text-slate-700">Doctor's full name</Label>
              <Input data-testid="edit-name-input" id="fix-name" value={newName} onChange={(e) => setNewName(e.target.value)} className="mt-1.5" />
            </div>
            <Button data-testid="save-name-btn" onClick={saveName} disabled={saving} className="w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save name"}
            </Button>
            <p className="text-xs text-slate-400 text-center">The wall and certificate will use the corrected name.</p>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
